import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { CustomDivider } from "@/components/CustomDivider";
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Community } from "@/types/community";
import CancelCreate from '@/components/CancelCreate';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
  },
  numeric: {
    width: 80,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
  },
});

function getVal(val: string, increment: boolean, ninetynine: boolean): string {
  let result = parseFloat(val);
  if (Number.isNaN(result)) {
    result = AppConsts.MIN_COVER_CHARGE;
  } else {
    if (increment) {
      result = Math.round(result) + 1;
    } else {
      result = Math.round(result) - 1;
    }
    if (ninetynine) {
      result -= 0.01;
    }
  }
  return "" + result;
}

function getCurrentVal(val: string): number {
  let result = parseFloat(val);
  if (Number.isNaN(result)) {
    result = AppConsts.MIN_COVER_CHARGE;
  }
  return result;
}

export default function CoverCharge() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const monthInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const { id } = useLocalSearchParams<{ id?: string }>();

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      for_men: "" + AppConsts.DEFAULT_COVER_CHARGE_MEN,
      for_women: "" + AppConsts.DEFAULT_COVER_CHARGE_WOMEN,
      first_free_men: '0',
      first_free_women: '0',
    }
  });

  const loadInitialData = useCallback(async () => {
    try {
      showSpinner();
      
      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.loadInitialData",
        "/rabbl/api/adminrabbl",
        "POST",
        {
          id: id,
        }
      );
      const communityResult: Community = ((response as any).body as any).community;

      setValue("for_men", communityResult.price_men + '');
      setValue("for_women", communityResult.price_women + '');
      setValue("first_free_men", communityResult.first_free_men + '');
      setValue("first_free_women", communityResult.first_free_women + '');

    } catch (e) {
      AppUtilities.handleException(e, "CoverCharge.loadInitialData");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
    } finally {
      hideSpinner();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadInitialData();
    }
  }, [id]);

  async function onSubmit(data: any) {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "CoverCharge.onSubmit",
        "/rabbl/api/set_cover_charge",
        "POST",
        {
          for_men: data.for_men,
          for_women: data.for_women,
          first_free_men: data.first_free_men,
          first_free_women: data.first_free_women,
          id: id,
        }
      );
      const creating_community: Community = ((response as any).body as any).creating_community;
      dispatch(setData(response));
      if (id && id.length > 0) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.navigate("/rabbls");
        }
      } else {
        router.navigate(AppUtils.getRabblCreateNextPageLink(creating_community, userInfo) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "CoverCharge.onSubmit");
    } finally {
      hideSpinner();
    }
  };

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (monthInput && monthInput.current) {
        monthInput.current?.focus();
      }
    }
  }, [monthInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Cover charge"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <Controller
            control={control}
            name="for_men"
            rules={{
              validate: (x) => {
                const i = parseFloat(x);
                if (x == '') {
                  return 'Cover charge for men is required.';
                } else if (Number.isNaN(i)) {
                  return 'Invalid number';
                } else if (i < AppConsts.MIN_COVER_CHARGE) {
                  return 'The minimum cover charge is $' + AppConsts.MIN_COVER_CHARGE;
                } else if (i > AppConsts.MAX_COVER_CHARGE) {
                  return 'The maximum cover charge is $' + AppConsts.MAX_COVER_CHARGE + '. Contact us if you want to go higher.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <CustomText>Cover charge for men</CustomText>
                <View style={styles.numericWrapper}>
                  <Button95
                    disabled={getCurrentVal(value) <= AppConsts.MIN_COVER_CHARGE}
                    onPress={() => onChange(getVal(value, false, true))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) <= AppConsts.MIN_COVER_CHARGE}
                      direction='left'
                    />
                  </Button95>
                  <AppText style={{ fontSize: 25 }}>$</AppText>
                  <TextInput
                    value={value}
                    onChangeText={onChange}
                    inputMode='numeric'
                    keyboardType='number-pad'
                    onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                    placeholder=''
                    textAlign='center'
                    style={styles.numeric}
                  />
                  <Button95
                    disabled={getCurrentVal(value) >= AppConsts.MAX_COVER_CHARGE}
                    onPress={() => onChange(getVal(value, true, true))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) >= AppConsts.MAX_COVER_CHARGE}
                      direction='right'
                    />
                  </Button95>
                </View>
              </View>
            )}
          />
          {errors.for_men && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.for_men.message} ^</AppText>}

          <CustomDivider fullWidth={true} />

          <Controller
            control={control}
            name="for_women"
            rules={{
              validate: (x) => {
                const i = parseFloat(x);
                if (x == '') {
                  return 'Cover charge for women is required.';
                } else if (Number.isNaN(i)) {
                  return 'Invalid number';
                } else if (i < AppConsts.MIN_COVER_CHARGE) {
                  return 'The minimum charge is $' + AppConsts.MIN_COVER_CHARGE;
                } else if (i > AppConsts.MAX_COVER_CHARGE) {
                  return 'The maximum charge is $' + AppConsts.MAX_COVER_CHARGE + '. Contact us if you want to go higher.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <CustomText>Cover charge for women</CustomText>
                <View style={styles.numericWrapper}>
                  <Button95
                    disabled={getCurrentVal(value) <= AppConsts.MIN_COVER_CHARGE}
                    onPress={() => onChange(getVal(value, false, true))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) <= AppConsts.MIN_COVER_CHARGE}
                      direction='left'
                    />
                  </Button95>
                  <AppText style={{ fontSize: 25 }}>$</AppText>
                  <TextInput
                    value={value}
                    onChangeText={onChange}
                    inputMode='numeric'
                    keyboardType='number-pad'
                    onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                    placeholder=''
                    textAlign='center'
                    style={styles.numeric}
                  />
                  <Button95
                    disabled={getCurrentVal(value) >= AppConsts.MAX_COVER_CHARGE}
                    onPress={() => onChange(getVal(value, true, true))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) >= AppConsts.MAX_COVER_CHARGE}
                      direction='right'
                    />
                  </Button95>
                </View>
              </View>
            )}
          />
          {errors.for_women && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.for_women.message} ^</AppText>}

          <CustomDivider fullWidth={true} />

          <Controller
            control={control}
            name="first_free_men"
            rules={{
              validate: (x) => {
                const i = parseInt(x);
                if (x == '') {
                  return undefined;
                } else if (Number.isNaN(i)) {
                  return 'Invalid number';
                } else if (i < 0) {
                  return 'Invalid number';
                } else if (i > AppConsts.MAX_FREE_MEMBERSHIPS) {
                  return 'The maximum free memberships is ' + AppConsts.MAX_FREE_MEMBERSHIPS + '. Contact us if you want to go higher.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <AppText>How many of the first men will have no cover charge?</AppText>
                <View style={styles.numericWrapper}>
                  <Button95
                    disabled={getCurrentVal(value) <= 0}
                    onPress={() => onChange(getVal(value, false, false))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) <= 0}
                      direction='left'
                    />
                  </Button95>
                  <TextInput
                    value={value}
                    onChangeText={onChange}
                    inputMode='numeric'
                    keyboardType='number-pad'
                    onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                    placeholder=''
                    textAlign='center'
                    style={styles.numeric}
                  />
                  <Button95
                    disabled={getCurrentVal(value) >= AppConsts.MAX_FREE_MEMBERSHIPS}
                    onPress={() => onChange(getVal(value, true, false))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) >= AppConsts.MAX_FREE_MEMBERSHIPS}
                      direction='right'
                    />
                  </Button95>
                </View>
              </View>
            )}
          />
          {errors.first_free_men && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.first_free_men.message} ^</AppText>}

          <Controller
            control={control}
            name="first_free_women"
            rules={{
              validate: (x) => {
                const i = parseInt(x);
                if (x == '') {
                  return undefined;
                } else if (Number.isNaN(i)) {
                  return 'Invalid number';
                } else if (i < 0) {
                  return 'Invalid number';
                } else if (i > AppConsts.MAX_FREE_MEMBERSHIPS) {
                  return 'The maximum free memberships is ' + AppConsts.MAX_FREE_MEMBERSHIPS + '. Contact us if you want to go higher.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <AppText>How many of the first women will have no cover charge?</AppText>
                <View style={styles.numericWrapper}>
                  <Button95
                    disabled={getCurrentVal(value) <= 0}
                    onPress={() => onChange(getVal(value, false, false))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) <= 0}
                      direction='left'
                    />
                  </Button95>
                  <TextInput
                    value={value}
                    onChangeText={onChange}
                    inputMode='numeric'
                    keyboardType='number-pad'
                    onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                    placeholder=''
                    textAlign='center'
                    style={styles.numeric}
                  />
                  <Button95
                    disabled={getCurrentVal(value) >= AppConsts.MAX_FREE_MEMBERSHIPS}
                    onPress={() => onChange(getVal(value, true, false))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={getCurrentVal(value) >= AppConsts.MAX_FREE_MEMBERSHIPS}
                      direction='right'
                    />
                  </Button95>
                </View>
              </View>
            )}
          />
          {errors.first_free_women && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.first_free_women.message} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Next</Button95>
          </View>
        </View>
      </Panel>
      
      { (id == null || id.length == 0) && <CancelCreate /> }
      
      <Footer />
    </BasicScrollView>
  );
}
